import {HttpClient} from '@sollapay/http-client';
import {
  Button,
  InfoRow,
  Popover,
  PopoverChevron,
  PopoverContent,
  PopoverTrigger,
  Typography
} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {useQuery} from '@tanstack/react-query';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import type {FC} from 'react';

export interface DebugModeButtonProps {
  environment: string;
  appVersion: string;
  apiUrl: string;
  ns?: string;
  className?: string;
  popoverAlign?: 'start' | 'center' | 'end';
}

export const DebugModeButton: FC<DebugModeButtonProps> = ({
  environment,
  appVersion,
  apiUrl,
  ns = 'common',
  className,
  popoverAlign = 'end'
}) => {
  const {t} = useTranslation(ns);
  const [open, setOpen] = useState(false);
  const {data: apiVersion} = useQuery({
    queryKey: ['debug-mode', 'api-version', apiUrl],
    queryFn: () => new HttpClient({baseUrl: apiUrl}).get<string>('/version'),
    enabled: open,
    staleTime: Infinity
  });

  const dotColorClass = environment === 'stage' ? 'bg-debug-env-stage' : 'bg-debug-env-dev';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="secondary"
            className={cn(
              'h-9 gap-3 rounded-md px-3 py-0 shadow-xs hover:bg-background-surface aria-expanded:bg-background-surface',
              className
            )}
          >
            <span
              className={cn(
                'size-1.75 shrink-0 rounded-full ring-2 ring-background-surface',
                dotColorClass
              )}
            />
            <span className="inline-flex items-baseline gap-1">
              <Typography as="span" size="sm" weight="medium" className="text-fg-secondary">
                {t('debugMode.label')}
              </Typography>
              <Typography as="span" size="sm" color="tertiary">
                · {environment}
              </Typography>
            </span>
            <PopoverChevron className="size-3" />
          </Button>
        }
      />

      <PopoverContent
        side="top"
        align={popoverAlign}
        sideOffset={8}
        className="w-[268px] overflow-hidden rounded-lg bg-background-surface p-0"
      >
        <div className="flex items-center gap-2 border-b border-border-subtle px-[13px] py-[11px]">
          <span className={cn('h-[13px] w-[3px] shrink-0 rounded-full', dotColorClass)} />
          <Typography as="span" size="sm" weight="semibold" className="font-display">
            {t('debugMode.label')}
          </Typography>
        </div>

        <div className="divide-y divide-border-subtle px-[13px] pt-1 pb-2.5">
          <InfoRow
            label={t('debugMode.environment')}
            value={environment}
            size="xs"
            mono
            className="py-1.5"
          />
          <InfoRow
            label={t('debugMode.version')}
            value={`v${appVersion}`}
            size="xs"
            mono
            dir="ltr"
            className="py-1.5"
          />
          <InfoRow
            label={t('debugMode.apiVersion')}
            value={apiVersion ? `v${apiVersion}` : '—'}
            size="xs"
            mono
            dir="ltr"
            className="py-1.5"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
